// ── Assignments tab — the weekly assignment submission log (Wave 3, REAL read) ─
// Reuses assignments.forCohort wrapped in useHonestQuery -> CohortTabShell.

import { CollectionPagination, CollectionToolbar, DataTableV2 } from "@vjal/ui";
import { useMemo } from "react";

import { useHonestQuery } from "../../../components/honest-state";
import { getCoachingZ6Services } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type { CoachingAssignmentLogDto } from "../../../services/coaching-z6-dtos";
import { CohortTabShell } from "./cohort-tab-shell";
import { mapAssignmentWeeks, useCohortAssignmentsCollection } from "./cohort-assignments-collection";

export function CohortAssignmentsTab({ cohortId }: { cohortId: string }) {
  const query = useHonestQuery<CoachingAssignmentLogDto>(
    coachingQueryKeys.z6.assignments(cohortId),
    () => getCoachingZ6Services().assignments.forCohort(cohortId),
    { isEmpty: (data) => (data.assignments?.weeks ?? []).length === 0 }
  );
  const allRows = useMemo(
    () => mapAssignmentWeeks(query.data?.assignments.weeks ?? []),
    [query.data?.assignments.weeks]
  );
  const collection = useCohortAssignmentsCollection(allRows);

  return (
    <CohortTabShell
      title="Assignments"
      subtitle="Per-week assignment submissions (Jotform-sourced). Unmatched rows mean the submitter email didn't match a participant."
      posture={query.posture}
      gate={query.gate}
      reference={query.reference}
      onRetry={query.retry}
      emptyTitle="No assignment submissions"
      emptyMessage="Weekly assignment submissions will appear here once participants submit."
    >
      <CollectionToolbar
        collection={collection}
        searchPlaceholder="Filter weeks"
        className="rounded-xl border border-line"
        stableChipNames
      />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2
          collection={collection}
          caption="Weekly assignment submissions"
          emptyTitle="No weeks match your search"
          emptyMessage="Clear or change the filters above to widen the view."
        />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </CohortTabShell>
  );
}